import { useState } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Briefcase, Store, Loader2, MailCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth, homeFor } from "../lib/auth";
import { Logo } from "../components/Chrome";
import { ChoiceCard, Spinner } from "../components/Bits";

export function AuthBox({ next, role, testid = "auth-box", initialMode = "register" }) {
  const { t } = useT();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = mode === "register" ? { ...f, role } : { email: f.email, password: f.password };
      const { data } = await api.post(`/auth/${mode}`, body);
      setUser(data);
      if (!data.role) return nav("/choose-role", { replace: true });
      const target = data.role === "admin" ? "/admin" : data.role === "employer" ? (next?.startsWith("/employer") ? next : "/employer") : next && !next.startsWith("/employer") ? next : "/start";
      nav(target, { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? t(mode === "register" ? "pw_rules" : "error") : errMsg(err));
    } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div className="w-full text-left" data-testid={testid}>
      <div className="mb-4 grid grid-cols-2 rounded-2xl bg-slate-100 p-1">
        {["register", "login"].map((m) => (
          <button key={m} type="button" onClick={() => { setMode(m); setError(""); }} data-testid={`${testid}-tab-${m}`}
            className={`min-h-[48px] rounded-xl font-bold transition-colors ${mode === m ? "bg-white text-[color:var(--navy)] shadow-sm" : "text-slate-600"}`}>
            {t(m === "register" ? "create_account" : "sign_in")}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-3">
        {mode === "register" && <><label className="label" htmlFor={`${testid}-name`}>{t("w_name")}</label><input id={`${testid}-name`} className="field" value={f.name} onChange={set("name")} autoComplete="name" required minLength={2} data-testid={`${testid}-name-input`} /></>}
        <label className="label" htmlFor={`${testid}-email`}>{t("email")}</label>
        <input id={`${testid}-email`} type="email" inputMode="email" className="field" value={f.email} onChange={set("email")} autoComplete="email" required data-testid={`${testid}-email-input`} />
        <label className="label" htmlFor={`${testid}-pw`}>{t("password")}</label>
        <input id={`${testid}-pw`} type="password" className="field" value={f.password} onChange={set("password")} autoComplete={mode === "register" ? "new-password" : "current-password"} required minLength={mode === "register" ? 8 : 1} data-testid={`${testid}-password-input`} />
        {mode === "register" && <p className="text-sm text-slate-500">{t("pw_rules")}</p>}
        {error && <p className="rounded-xl bg-red-50 p-3 font-semibold text-red-700" data-testid={`${testid}-error`}>{error}</p>}
        <button className="btn-navy !w-full" disabled={busy} data-testid={`${testid}-submit-btn`}>{busy && <Loader2 className="animate-spin" />}{t(mode === "register" ? "create_account" : "sign_in")}</button>
        {mode === "login" && <Link to="/forgot-password" className="block min-h-[44px] pt-2 text-center font-semibold text-[color:var(--orange-dark)] underline-offset-4 hover:underline" data-testid={`${testid}-forgot-link`}>{t("forgot_pw")}</Link>}
      </form>
    </div>
  );
}

export function Login() {
  const { t } = useT();
  const { user, loading } = useAuth();
  const [sp] = useSearchParams();
  if (loading) return <Spinner />;
  if (user) return <Navigate to={sp.get("next") || homeFor(user)} replace />;
  return (
    <div className="narrow flex flex-col items-center py-12 text-center">
      <Logo size={120} testid="login-logo" />
      <h1 className="mt-6 text-3xl font-bold text-[color:var(--navy)]">{t("login_t")}</h1>
      <p className="mt-2 text-slate-600">{t("login_sub")}</p>
      {sp.get("error") && <p className="mt-4 rounded-xl bg-red-50 p-3 font-semibold text-red-700" data-testid="login-error">{t("error")}</p>}
      <div className="mt-8 w-full"><AuthBox next={sp.get("next")} role={sp.get("role") || undefined} initialMode={sp.get("role") === "candidate" ? "register" : "login"} testid="login" /></div>
    </div>
  );
}

export function ForgotPassword() {
  const { t, lang } = useT();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot", { email, lang }); setSent(true); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="narrow py-12">
      <div className="flex justify-center"><Logo size={88} testid="forgot-logo" /></div>
      <h1 className="mt-6 text-center text-3xl font-bold text-[color:var(--navy)]">{t("forgot_t")}</h1>
      {sent ? (
        <div className="card-soft mt-8 flex flex-col items-center p-8 text-center" data-testid="forgot-sent">
          <MailCheck size={56} className="text-emerald-500" />
          <p className="mt-3 text-lg font-bold">{t("forgot_sent_t")}</p>
          <p className="text-slate-600">{t("forgot_sent_sub")}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-3">
          <p className="text-slate-600">{t("forgot_sub")}</p>
          <label className="label" htmlFor="forgot-email">{t("email")}</label>
          <input id="forgot-email" type="email" inputMode="email" required className="field" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          <button className="btn-orange !w-full" disabled={busy} data-testid="forgot-submit-btn">{busy && <Loader2 className="animate-spin" />}{t("forgot_btn")}</button>
        </form>
      )}
      <Link to="/login" className="btn-ghost mt-4 !w-full" data-testid="forgot-back-login">{t("sign_in")}</Link>
    </div>
  );
}

export function ResetPassword() {
  const { t } = useT();
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { await api.post("/auth/reset", { token: sp.get("token") || "", password: pw }); setDone(true); } catch (err) { setError(err?.response?.status === 422 ? t("pw_rules") : errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="narrow py-12">
      <div className="flex justify-center"><Logo size={88} testid="reset-logo" /></div>
      <h1 className="mt-6 text-center text-3xl font-bold text-[color:var(--navy)]">{t("reset_t")}</h1>
      {done ? (
        <div className="card-soft mt-8 flex flex-col items-center p-8 text-center" data-testid="reset-done">
          <CheckCircle2 size={56} className="text-emerald-500" />
          <p className="mt-3 text-lg font-bold">{t("reset_done")}</p>
          <Link to="/login" className="btn-orange mt-6 !w-full" data-testid="reset-login-btn">{t("sign_in")}</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-3">
          <label className="label" htmlFor="reset-pw">{t("new_password")}</label>
          <input id="reset-pw" type="password" autoComplete="new-password" required minLength={8} className="field" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
          <p className="text-sm text-slate-500">{t("pw_rules")}</p>
          {error && <p className="rounded-xl bg-red-50 p-3 font-semibold text-red-700" data-testid="reset-error">{error}</p>}
          <button className="btn-orange !w-full" disabled={busy} data-testid="reset-submit-btn">{busy && <Loader2 className="animate-spin" />}{t("save")}</button>
        </form>
      )}
    </div>
  );
}

export function ChooseRole() {
  const { t } = useT();
  const { user, loading, setUser } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role) return <Navigate to={homeFor(user)} replace />;
  const pick = async (role) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/role", { role });
      setUser(data);
      nav(role === "candidate" ? "/start" : "/employer/setup", { replace: true });
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="narrow py-10">
      <div className="flex justify-center"><Logo size={88} testid="role-logo" /></div>
      <h1 className="mt-6 text-center text-3xl font-bold text-[color:var(--navy)]">{t("choose_role_t")}</h1>
      <div className="mt-8 space-y-4">
        <ChoiceCard icon={Briefcase} label={t("role_cand")} sub={t("role_cand_d")} onClick={() => !busy && pick("candidate")} testid="role-candidate-card" />
        <ChoiceCard icon={Store} label={t("role_emp")} sub={t("role_emp_d")} onClick={() => !busy && pick("employer")} testid="role-employer-card" />
      </div>
    </div>
  );
}
